import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../../common/Button";
import Image from "../../common/Image";
import uklogo from "../../../assets/images/UK-logo.png";
import {
  BarsIcon,
  ChevronDownIcon,
  TimesIcon,
} from "../../../icons/CommonIcons";
import { navItems } from "../../../config/homePageUtils";
import { useLanguage } from "../../LanguageProvider/LanguageProvider";

const loginOptions = [
  {
    label: { en: "Investor", hi: "निवेशक" },
    to: "/login",
  },
  {
    label: { en: "Department", hi: "विभाग" },
    to: "/admin",
  },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const { language, setLanguage } = useLanguage();

  const handleNavClick = (sectionId) => {
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
    setIsOpen(false);
  };

  const scrollToSection = (id) => {
    if (!id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-transparent px-4 py-3 sm:py-4 z-40">
      <div className="mx-auto flex items-center justify-between px-8">
        {/* Logo */}
        <Image
          src={uklogo}
          alt="UK Logo"
          className="w-40 sm:w-24 md:w-32 lg:w-40 xl:w-48 object-contain"
        />

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8 rounded-full bg-[#4f4f4f66] px-10 py-4 backdrop-blur-[10px] backdrop-saturate-180 mr-2 ml-2">
          {navItems.map((item, i) => (
            <button
              key={i}
              onClick={() => handleNavClick(item.sectionId)}
              className={`cursor-pointer uppercase mr-3 transition text-sm hover:text-[#befaff] ${
                item.active ? "font-bold text-[#befaff]" : "text-white"
              }`}
            >
              {language ? item.label.hi : item.label.en}
            </button>
          ))}
        </div>
        {/* Login Dropdown Button */}
        <div className="relative" ref={dropdownRef}>
          <Button
            onClick={() => setShowDropdown(!showDropdown)}
            className="bg-white text-white px-4 py-2 rounded-full flex items-center whitespace-nowrap hover:opacity:90"
          >
            <span className="inline-block text-theme-color mx-6">
              {language ? "लॉगिन करें" : "Login as"}
            </span>
            <ChevronDownIcon fill="#018f9b" />
          </Button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <ul className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
              {loginOptions.map((option, index) => (
                <li
                  key={index}
                  onClick={() => navigate(option.to)}
                  rel="noopener noreferrer"
                  className="cursor-pointer w-full text-left px-4 py-2 text-gray-700 hover:bg-[#018f9b] hover:text-white transition-colors duration-150 rounded-md"
                >
                  {language ? option.label.hi : option.label.en}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Mobile Menu Icon */}
        <Button
          className="flex lg:hidden text-2xl"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <TimesIcon className="w-6 h-6" fill="#000" />
          ) : (
            <BarsIcon className="w-6 h-6" fill="#000" />
          )}
        </Button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden mt-3 w-full">
          <ul className="bg-gray-100 shadow-lg leading-9 font-bold py-4 rounded">
            {navItems.map((item, i) => (
              <li key={i} className="border-b border-gray-200 last:border-none">
                <button
                  onClick={() => handleNavClick(item.sectionId)}
                  className="block w-full text-left px-6 py-2 text-sm uppercase hover:bg-[#018f9b] hover:text-white"
                >
                  {language ? item.label.hi : item.label.en}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
